import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, MapPin, Info } from 'lucide-react';
import { Link, useNavigate } from 'react-router';
import { formatDate } from '@/lib/utils';

// Define the record type
interface Record {
  id: string;
  userId: string;
  orgId: string;
  speciesId: string;
  dtm: string;
  lat?: number;
  lng?: number;
  locationNote?: string;
  note?: string;
  siteId?: string;
  count?: number;
  createdAt: string;
  updatedAt: string;
  species: {
    id: string;
    scientificName: string;
    englishName: string;
  };
  organisation: {
    id: string;
    name: string;
  };
}

export const AdminRecords: React.FC = () => {
  const navigate = useNavigate();
  // Fetch records
  const { data: records, isLoading, error } = useQuery({
    queryKey: ['records'],
    queryFn: async () => {
      const response = await fetch('/api/record');
      if (!response.ok) {
        throw new Error('Failed to fetch records');
      }
      return response.json() as Promise<Record[]>;
    },
  });

  return (
    <div className="container mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Records</h1>
      </div>
      <div>
        {isLoading ? (
          <div className="flex justify-center p-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : error ? (
          <div className="bg-red-50 p-4 rounded-md text-red-800">
            Failed to load records. Please try again.
          </div>
        ) : (
          <div className="space-y-4">
            {records && records.length > 0 ? (
              <div className="grid gap-4">
                {records.map((record) => {
                  return (
                    <Card key={record.id}>
                      <CardHeader>
                        <div className="flex justify-between">
                          <div className="flex items-center">
                            <CardTitle>{record.species.englishName || 'Unknown Species'}</CardTitle>
                          </div>
                          <Link to={`/app/admin/record/${record.id}`}>
                            <Button
                              variant="ghost"
                              size="sm"
                            >
                              <Info className="h-4 w-4 mr-1" />
                              View Details
                            </Button>
                          </Link>
                        </div>
                        <CardDescription className="italic">{record.species.scientificName}</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <span className="text-sm font-medium">Date:</span>
                            <p className="text-sm text-muted-foreground">{formatDate(record.dtm)}</p>
                          </div>

                          {record.count !== undefined && (
                            <div>
                              <span className="text-sm font-medium">Count:</span>
                              <p className="text-sm text-muted-foreground">{record.count}</p>
                            </div>
                          )}

                          {(record.lat !== undefined && record.lng !== undefined) && (
                            <div className="col-span-2 flex items-center">
                              <MapPin className="h-4 w-4 mr-1 text-muted-foreground" />
                              <span className="text-sm text-muted-foreground">
                                {record.lat && record.lng ? (
                                  `${record.lat.toFixed(6)}, ${record.lng.toFixed(6)}`
                                ) : (
                                  "Coordinates not available"
                                )}
                              </span>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-4">
                No records found
              </div>
            )}
          </div>
        )}
      </div>
    </div >
  );
};

